import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState } from "@/components/shared";
import { toast } from "sonner";
import { applyBranding } from "@/hooks/useBranding";
import Governance from "./tabs/Governance";
import UsersRoles from "./tabs/Users";
import AuditRights from "./tabs/AuditRights";
import BiasMonitor from "./tabs/BiasMonitor";
import AiSettings from "./tabs/AiSettings";

const TAB_TITLES = {
  governance: "Overview",
  users: "Users & Roles",
  audit: "Audit & Rights",
  settings: "AI & Retention",
  bias: "Bias Monitor",
};

export default function Admin({ tab = "governance" }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [rights, setRights] = useState([]);
  const [bias, setBias] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    Promise.all([
      api.get("/admin/settings"), api.get("/admin/users"),
      api.get("/admin/audit"), api.get("/admin/rights-requests"), api.get("/admin/bias"),
      api.get("/admin/insights"),
    ]).then(([s, u, a, r, b, i]) => { setSettings(s.data); setUsers(u.data); setAudit(a.data); setRights(r.data); setBias(b.data); setInsights(i.data); })
      .catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveSetting = async (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    try { await api.put("/admin/settings", { key, value }); toast.success("Setting updated & logged"); }
    catch { toast.error("Could not save"); load(); }
  };

  const saveBrand = async (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    if (key === "brand_primary") applyBranding({ brand_primary: value });
    try { await api.put("/admin/settings", { key, value }); window.dispatchEvent(new Event("branding-updated")); toast.success("Branding updated"); }
    catch { toast.error("Could not save"); }
  };

  const resetBrand = async () => {
    try {
      const { data } = await api.delete("/org/branding");
      setSettings((s) => ({ ...s, ...data }));
      applyBranding(data);
      window.dispatchEvent(new Event("branding-updated"));
      toast.success("Branding reset to HireStream default");
    } catch { toast.error("Could not reset"); }
  };

  const resolveRight = async (id, decision) => {
    try {
      await api.patch(`/admin/rights-requests/${id}`, { decision });
      toast.success(`Request ${decision}`);
      setRights((rs) => rs.map((r) => (r.id === id ? { ...r, status: decision } : r)));
    } catch { toast.error("Could not update"); }
  };

  if (loading) return <LoadingState label="Loading governance console…" />;
  if (err) return <div className="p-8"><ErrorState message={err} onRetry={load} /></div>;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{user?.role === "super_admin" ? "Platform governance · all organizations" : "Organization governance"}</div>
        <h1 className="text-3xl font-display font-bold tracking-tight">{TAB_TITLES[tab] || "Overview"}</h1>
      </div>

      {tab === "governance" && <Governance insights={insights} />}
      {tab === "users" && <UsersRoles users={users} />}
      {tab === "audit" && <AuditRights rights={rights} audit={audit} onResolve={resolveRight} />}
      {tab === "settings" && settings && <AiSettings settings={settings} saveSetting={saveSetting} saveBrand={saveBrand} resetBrand={resetBrand} />}
      {tab === "bias" && <BiasMonitor bias={bias} />}
    </div>
  );
}
